import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { supabase } from '../../lib/supabase';
import { Sparkles, X, Copy, Check } from 'lucide-react';
import styles from './ChatDrawer.module.css';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const SESSION_KEY = 'vennio-app-chat';
const CHAT_VERSION = '1';

// --- Markdown renderer ---

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <button className={styles.copyButton} onClick={handleCopy}>
      {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
    </button>
  );
}

function renderMarkdown(text) {
  if (!text) return null;

  const parts = text.split(/(```[\s\S]*?```)/g);

  return parts.map((part, i) => {
    const codeMatch = part.match(/^```(\w*)\n?([\s\S]*?)```$/);
    if (codeMatch) {
      const code = codeMatch[2].replace(/\n$/, '');
      return (
        <div key={i} className={styles.codeBlockWrap}>
          <pre className={styles.codeBlock}>
            <code>{code}</code>
          </pre>
          <CopyButton text={code} />
        </div>
      );
    }

    return <span key={i}>{renderInline(part)}</span>;
  });
}

function renderInline(text) {
  const tokens = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g);

  return tokens.map((token, i) => {
    if (token.startsWith('`') && token.endsWith('`')) {
      return (
        <code key={i} className={styles.inlineCode}>
          {token.slice(1, -1)}
        </code>
      );
    }
    if (token.startsWith('**') && token.endsWith('**')) {
      return <strong key={i}>{token.slice(2, -2)}</strong>;
    }
    return token;
  });
}

// --- Context-aware starter questions ---

function getBusinessStarters({ calendarConnected, hasConnections, hasMatches }) {
  if (!calendarConnected) {
    return [
      'Help me connect my calendar',
      'What is Vennio and how does it work?',
      'How do I invite customers?',
      'What should I do next?',
    ];
  }
  if (!hasConnections) {
    return [
      'How do I invite customers?',
      'Help me create a Venn Link',
      "What's my Business ID and API credentials?",
      'What should I do next?',
    ];
  }
  if (!hasMatches) {
    return [
      'Find matching availability',
      'Help me create a Venn Link',
      'How do I view my analytics?',
      'What should I do next?',
    ];
  }
  return [
    'Help me send an offer',
    'How do I manage my network?',
    'How do I view my analytics?',
    'What should I do next?',
  ];
}

function getCustomerStarters({ calendarConnected, hasBusinesses }) {
  if (!calendarConnected) {
    return [
      'Help me connect my calendar',
      'What is Vennio and how does it work?',
      'How do I find businesses?',
      'What should I do next?',
    ];
  }
  if (!hasBusinesses) {
    return [
      'How do I find and opt into businesses?',
      'How do I book an appointment?',
      'How do I manage my preferences?',
      'What should I do next?',
    ];
  }
  return [
    'How do I book an appointment?',
    'Where are my upcoming bookings?',
    'How do I manage my preferences?',
    'What should I do next?',
  ];
}

// --- Message Bubble ---

function FeedbackButtons({ message, onFeedback }) {
  const [voted, setVoted] = useState(message.feedback || null);
  const [sending, setSending] = useState(false);

  async function handleVote(value) {
    if (voted || sending || !message.messageId) return;
    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetch(`${API_URL}/v1/ai/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          messageId: message.messageId,
          feedback: value,
          pagePath: window.location.pathname,
        }),
      });
      setVoted(value);
      onFeedback(message.messageId, value);
    } catch {
      // silently fail
    } finally {
      setSending(false);
    }
  }

  if (message.streaming || !message.messageId) return null;

  return (
    <div className={styles.feedbackRow}>
      <button
        onClick={() => handleVote(1)}
        disabled={!!voted || sending}
        className={`${voted === 1 ? styles.feedbackButtonUpActive : styles.feedbackButtonUp} ${voted && voted !== 1 ? styles.feedbackButtonFaded : ''}`}
        title="Helpful"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"/>
        </svg>
      </button>
      <button
        onClick={() => handleVote(-1)}
        disabled={!!voted || sending}
        className={`${voted === -1 ? styles.feedbackButtonDownActive : styles.feedbackButtonDown} ${voted && voted !== -1 ? styles.feedbackButtonFaded : ''}`}
        title="Not helpful"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z"/>
        </svg>
      </button>
    </div>
  );
}

function MessageBubble({ message, onFeedback }) {
  const isUser = message.role === 'user';
  return (
    <div className={`${styles.messageBubbleRow} ${isUser ? styles.messageBubbleRowUser : ''}`}>
      {!isUser && (
        <div className={styles.assistantIcon}>
          <Sparkles size={12} />
        </div>
      )}
      <div>
        <div className={`${styles.messageBubble} ${isUser ? styles.messageBubbleUser : styles.messageBubbleAssistant}`}>
          {isUser ? message.content : renderMarkdown(message.content)}
          {message.streaming && (
            <span className={styles.cursor} />
          )}
        </div>
        {!isUser && <FeedbackButtons message={message} onFeedback={onFeedback} />}
      </div>
    </div>
  );
}

// --- Main Drawer ---

export default function ChatDrawer({ open, onClose, activeTab, calendarConnected, hasConnections, hasMatches, hasBusinesses }) {
  const { profile } = useAuth();
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(SESSION_KEY);
      if (!saved) return [];
      const parsed = JSON.parse(saved);
      if (parsed.version !== CHAT_VERSION) return [];
      return parsed.messages || [];
    } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Sync messages to sessionStorage
  useEffect(() => {
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify({ version: CHAT_VERSION, messages: messages.filter(m => !m.streaming) }));
    } catch {}
  }, [messages]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    localStorage.removeItem(SESSION_KEY);
  }, []);

  async function sendMessage(text) {
    const userMessage = text || input.trim();
    if (!userMessage || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    setMessages(prev => [...prev, { role: 'assistant', content: '', streaming: true }]);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const response = await fetch(`${API_URL}/v1/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          messages: [
            ...messages.filter(m => !m.streaming),
            { role: 'user', content: userMessage }
          ],
          activeTab,
          currentPath: window.location.pathname,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') break;
          try {
            const { text } = JSON.parse(data);
            if (text) {
              setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last.role === 'assistant') {
                  updated[updated.length - 1] = { ...last, content: last.content + text };
                }
                return updated;
              });
            }
          } catch {}
        }
      }
    } catch (err) {
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: 'assistant',
          content: 'Sorry, something went wrong. Please try again.',
        };
        return updated;
      });
    } finally {
      setMessages(prev => prev.map(m =>
        m.streaming ? { ...m, streaming: false, messageId: m.messageId || (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`) } : m
      ));
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  const starters = profile?.role === 'customer'
    ? getCustomerStarters({ calendarConnected, hasBusinesses })
    : getBusinessStarters({ calendarConnected, hasConnections, hasMatches });

  if (!open) return null;

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />

      <div className={styles.drawer}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}>
              <Sparkles size={14} />
            </div>
            <div>
              <p className={styles.headerTitle}>Ask AI</p>
              <p className={styles.headerSubtitle}>Powered by Claude</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            {messages.length > 0 && (
              <button className={styles.clearButton} onClick={clearMessages}>
                Clear
              </button>
            )}
            <button className={styles.closeButton} onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className={styles.messagesArea}>
          {messages.length === 0 ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyPrompt}>Ask anything about using Vennio</p>
              <div className={styles.starters}>
                {starters.map(s => (
                  <button
                    key={s}
                    onClick={() => sendMessage(s)}
                    className={styles.starterButton}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              {messages.map((m, i) => (
                <MessageBubble key={m.messageId || i} message={m} onFeedback={(msgId, value) => {
                  setMessages(prev => prev.map(msg =>
                    msg.messageId === msgId ? { ...msg, feedback: value } : msg
                  ));
                }} />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input */}
        <div className={styles.inputArea}>
          <div className={styles.inputWrap}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about using Vennio..."
              rows={1}
              className={styles.input}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              className={styles.sendButton}
            >
              {loading ? (
                <span className={styles.spinner} />
              ) : (
                <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
          </div>
          <p className={styles.inputHint}>Press Enter to send · Shift+Enter for new line</p>
        </div>
      </div>
    </>
  );
}
